import { Input, Stack, Text, createListCollection } from "@chakra-ui/react";
import * as React from "react";

import { getFacebookDatasets } from "../api/integration";
import { useLoading, useToaster } from "../hooks";
import { FacebookDataset } from "../types/facebook";
import { Field } from "./field";
import Link from "./link";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "./select";

interface FacebookCapiCredentialsFieldsProps {
  workspaceSlug: string;
  adAccountId: string;
  dataset: string;
  onDatasetChange: (id: string) => void;
  accessToken: string;
  onAccessTokenChange: (token: string) => void;
  portalRef: React.RefObject<HTMLElement>;
  datasetHelperText?: React.ReactNode;
}

export function FacebookCapiCredentialsFields({
  workspaceSlug,
  adAccountId,
  dataset,
  onDatasetChange,
  accessToken,
  onAccessTokenChange,
  portalRef,
  datasetHelperText,
}: FacebookCapiCredentialsFieldsProps) {
  const [datasets, setDatasets] = React.useState<FacebookDataset[]>([]);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();

  const datasetsCollection = React.useMemo(() => {
    return createListCollection({
      items: datasets.map((d) => ({ value: d.id, label: d.name })),
    });
  }, [datasets]);

  React.useEffect(() => {
    if (!adAccountId) return;
    withLoading(async () => {
      setDatasets([]);
      await withToaster(async () => {
        const data = await getFacebookDatasets(workspaceSlug, adAccountId);
        setDatasets(data);
      });
    });
  }, [workspaceSlug, adAccountId, withLoading, withToaster]);

  return (
    <Stack gap="4">
      <Field label="Dataset" required helperText={datasetHelperText}>
        {isLoading && datasets.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">
            Loading datasets...
          </Text>
        ) : datasets.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">
            No datasets found for this ad account.
          </Text>
        ) : (
          <SelectRoot
            size="sm"
            value={dataset ? [dataset] : []}
            onValueChange={({ value }) => onDatasetChange(value[0] ?? "")}
            collection={datasetsCollection}
          >
            <SelectTrigger>
              <SelectValueText placeholder="Select a dataset" />
            </SelectTrigger>
            <SelectContent portalRef={portalRef}>
              {datasetsCollection.items.map((item) => (
                <SelectItem key={item.value} item={item}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        )}
      </Field>
      <Field
        label="Access Token"
        required
        helperText={
          dataset ? (
            <>
              Go to your{" "}
              <Link
                to={`https://eventsmanager.facebook.com/events_manager2/list/app/${dataset}/overview?act=${adAccountId.replace("act_", "")}`}
                target="_blank"
                textDecoration="underline"
              >
                dataset settings in Events Manager
              </Link>
              , scroll to Conversions API, and click Generate access token.
            </>
          ) : (
            "Select a dataset above, then generate an access token in Meta Events Manager."
          )
        }
      >
        <Input
          size="sm"
          type="password"
          placeholder="Paste your access token"
          value={accessToken}
          onChange={(e) => onAccessTokenChange(e.target.value)}
        />
      </Field>
    </Stack>
  );
}
